import { Row, Col, Empty, Spin, Button, Radio, Popover, Avatar, Divider, Checkbox, Pagination, Tag, message } from 'antd';
import { LinkOutlined, ExclamationCircleFilled, CaretUpOutlined, CaretDownOutlined } from '@ant-design/icons';

import { Link } from 'react-router-dom';
import moment from 'moment';
import config from '../../../config';
import { useTranslation } from 'react-i18next';
import countrySettings from '../countrySettings.json';

const { format } = require('rut.js');

const SortableHeader = ({ title, columnKey, currentSort, onSort }) => {
  const isAsc = currentSort?.key === columnKey && currentSort?.order === 'asc';
  const isDesc = currentSort?.key === columnKey && currentSort?.order === 'desc';

  return (
    <div style={{ display: 'flex',  cursor: 'pointer' }}>
      <span onClick={() => onSort(columnKey, isAsc ? 'desc' : 'asc')}>
        <h3 className="text-center">{title}</h3>
      </span>
      <div style={{ marginLeft: 0, display: 'flex', flexDirection: 'column' }}>
        <CaretUpOutlined
          style={{
            fontSize: 12,
            color: isAsc ? '#1677ff' : '#aaa',
            marginBottom: -1,
          }}
          onClick={() => onSort(columnKey, 'asc')}
        />
        <CaretDownOutlined
          style={{
            fontSize: 12,
            color: isDesc ? '#1677ff' : '#aaa',
            marginTop: -1,
          }}
          onClick={() => onSort(columnKey, 'desc')}
        />
      </div>
    </div>
  );
};

const ReevaluationTable = ({ title, columns, data, loading, pagination, filters, onPageChange, onOrderChange,countryCode, showActions }) => {
  const { t } = useTranslation();
  const publicSites = {
    CL: config.publicSiteUrlChile,
    PE: config.publicSiteUrlPeru,
  };

  const getDeferredExamAdmissionProgress = (item) => {
    if (!item.has_deferred_exam || !item.deferred_exam_extra_data) return null;
    const rawData = item.deferred_exam_extra_data;
    const parsedData = typeof rawData === 'string' ? JSON.parse(rawData) : rawData;
    const extraData = Array.isArray(parsedData) ? parsedData : [];
    const deferredEntry = extraData.find(entry => entry.selected_exam_ids);
    if (!deferredEntry) return null;
    const pendingExams = deferredEntry.selected_exam_ids?.length || 0;
    const admittedExams = deferredEntry.admitted_exam_ids?.length || 0;
    if (admittedExams === 0) return null;
    return { admittedExams, totalExams: admittedExams + pendingExams };
  };

  const getReevaluationExamNames = (item) => {
    if (item.deferred_exam_names && item.deferred_exam_names.length > 0) {
      return item.deferred_exam_names;
    }
    try {
      const extraData = item.deferred_exam_extra_data;
      if (!extraData) return [];
      const data = typeof extraData === 'string' ? JSON.parse(extraData) : extraData;
      const deferredEntry = Array.isArray(data) ? data.find(entry => entry.selected_exam_ids) : null;
      return deferredEntry?.selected_exam_names || deferredEntry?.selected_exam_ids?.map(id => `Examen #${id}`) || [];
    } catch {
      console.error('Failed fetching reevaluation exam names')
      message.error({
          content: 'Ha ocurrido un error al obtener los nombres de exámenes a reevaluar.',
          style: { marginTop: '35vh' },
        });
      return [];
    }
  };

  const getObservationLiftExamNames = (item) => {
    if (item.prescription_item_names && item.prescription_item_names.length > 0) {
      return item.prescription_item_names;
    }
    try {
      const extraData = item.prescription_extra_data;
      if (!extraData) return [];
      const data = typeof extraData === 'string' ? JSON.parse(extraData) : extraData;
      return Array.isArray(data) ? data.map(entry => entry.name).filter(Boolean) : [];
    } catch {
      return [];
    }
  };

  const formatDate = (date) => (date ? moment(date).format('DD-MM-YYYY') : '-');
  const reevaluationDatesPopoverContent = (originAdmissionDate, reevaluationDates) => (
    <div>
      {originAdmissionDate && (
        <Col>
          <Row className="mb-10">
            <strong>El paciente cuenta con el siguiente historial</strong>
          </Row>
          <Row>
            <Col lg={12}>
              <Row className="mb-10">
                <strong>Atención de origen</strong>
              </Row>
              {formatDate(originAdmissionDate)}
            </Col>
            <Col lg={12}>
              <Row className="mb-10">
                <strong>Reevaluaciones</strong>
              </Row>
              <Row className="mb-10">
                {reevaluationDates?.length > 0 ? (
                  reevaluationDates.map((date, index) => (
                    <Col xs={24} lg={24} key={index}>
                      {formatDate(date)}
                    </Col>
                  ))
                ) : (
                  <Col>-</Col>
                )}
              </Row>
            </Col>
          </Row>
        </Col>
      )}
    </div>
  );

  const documentDetailsPopoverContent = (documents) => (
    <div>
      {documents?.length > 0 ? (
        documents.map((document, index) => (
          <Col key={index}>
            <Row className="mb-5">
              <Col>
                <Checkbox checked={document?.state && document?.state !== 'pending_upload'} className="mb-5 cmt-checkbox">
                  <p>{document.name}</p>
                </Checkbox>
              </Col>
            </Row>
          </Col>
        ))
      ) : (
        <p></p>
      )}
    </div>
  );

  const renderCellValue = (item, key) => {
    switch (key) {
      case 'full_name':
        return (
          <div className="patient-name-container">
            <div className="patient-name-wrapper">
              {countrySettings.reevaluationList.showAttendanceLegend?.[countryCode] && item.presence_required !== undefined && (
                <div 
                  className={`attendance-legend-dot ${item.presence_required ? 'in-person' : 'remote'}`}
                />
              )}
              <span>{item.full_name}</span>
            </div>
            {countrySettings.reevaluationList.showPrescriptionTypeTag?.[countryCode] && (
              <>
                {item.has_deferred_exam && (
                  <Tag className="cmt-tag cmt-tag-outline-secondary">
                    {t('reservations.reevaluationList.reevaluationLabel')}
                  </Tag>
                )}
                {item.has_prescription && (
                  <Tag className="cmt-tag cmt-tag-outline-warning">
                    {t('reservations.reevaluationList.observationLiftLabel')}
                  </Tag>
                )}
              </>
            )}
          </div>
        );
      case 'document_number':
        return item.document_type === 'rut' ? format(item.document_number) : item.document_number;
      case 'date':
      case 'last_evaluation_date':
        return (
          <Col>
            <Row className="mb-5">
              {item?.last_evaluation_date ? formatDate(item[key]) : formatDate(item['origin_admission_date'])}
              <Popover
                title={<h3>Historial de evaluaciones</h3>}
                trigger="hover"
                content={<b>{reevaluationDatesPopoverContent(item?.origin_admission_date, item?.reevaluation_dates)}</b>}
                placement="bottom"
                overlayClassName="reevaluation-dates-popover"
              >
                {item?.reevaluation_dates?.length > 0 && (
                  <Avatar size="small" className="total-reevaluations-circle ml-5">
                    {item?.reevaluation_dates?.length > 0 ? item?.reevaluation_dates?.length : ''}
                  </Avatar>
                )}
              </Popover>
            </Row>
          </Col>
        );
      case 'days_to_expire':
        return item.days_to_expire + ' días';
      case 'days_since_issued':
        return item.days_since_issued === 0 || item.days_since_issued === null ? '-' : `${item.days_since_issued} ${t('reservations.reevaluationList.ageDaysLabel')}`;
      case 'outsourcing_company_name':
        return item.outsourcing_company_name || '-';
      case 'prestation': {
        const reevaluationNames = getReevaluationExamNames(item);
        const liftNames = getObservationLiftExamNames(item);
        const showPrestationPopover = countrySettings.reevaluationList.showPrestationPopover[countryCode];
        if ((reevaluationNames.length > 0 || liftNames.length > 0) && showPrestationPopover) {
          return (
            <Popover
              overlayClassName="prestation-popover"
              content={
                <div>
                  <div className="prestation-popover-section-title">Reevaluación por:</div>
                  {reevaluationNames.length > 0 ? (
                    <ul className="prestation-popover-list">
                      {reevaluationNames.map((name, i) => <li key={i}>{name}</li>)}
                    </ul>
                  ) : (
                    <div className="prestation-popover-empty">- Ninguno</div>
                  )}
                  <Divider className="prestation-popover-divider" />
                  <div className="prestation-popover-section-title">Levantamiento por:</div>
                  {liftNames.length > 0 ? (
                    <ul className="prestation-popover-list">
                      {liftNames.map((name, i) => <li key={i}>{name}</li>)}
                    </ul>
                  ) : (
                    <div className="prestation-popover-empty">- Ninguno</div>
                  )}
                </div>
              }
              trigger="hover"
            >
              <span className="prestation-trigger">
                {item.prestation ?? '-'}
              </span>
            </Popover>
          );
        }
        return item.prestation ?? '-';
      }
      case 'prescription_type': {
        const admissionProgress = getDeferredExamAdmissionProgress(item);
        return (
          <Col>
            {item.has_deferred_exam && (
              <Tag className="cmt-tag cmt-tag-outline-secondary">
                {t('reservations.reevaluationList.reevaluationLabel')}
              </Tag>
            )}
            {item.has_prescription && (
              <Tag className="cmt-tag cmt-tag-outline-warning">
                {t('reservations.reevaluationList.observationLiftLabel')}
              </Tag>
            )}
            {admissionProgress && (
              <p className="mt-5 text-center">
                <small>{admissionProgress.admittedExams}/{admissionProgress.totalExams} admitidos</small>
              </p>
            )}
          </Col>
        );
      }
      case 'documents':
        return (
          <Col>
            {item?.documents?.length > 0 && (
              <Row className="mb-5">
                <Popover
                  title={<h3>Detalle de documentos</h3>}
                  trigger="hover"
                  content={<b>{documentDetailsPopoverContent(item?.documents)}</b>}
                  placement="bottom"
                  overlayClassName="document-details-popover"
                >
                  <ExclamationCircleFilled className="mr-5 document-details-icon" />
                </Popover>
                <p
                  className={`mb-0 ${item.uploaded_documents === 0 && 'disabled'} ${
                    item.uploaded_documents === item.total_documents ? 'success' : 'error'
                  }`}
                >
                  {item.uploaded_documents === item.total_documents ? 'Completo ' : 'Incompleto '}
                  {item.uploaded_documents}/{item.total_documents}
                </p>
              </Row>
            )}
          </Col>
        );
      case 'presence_required':
        return item.presence_required ? 'Sí' : 'No';
      case 'upload_history': {
        if (!item.documents?.length) return null;
        const latestDocument = item.documents.reduce((latest, doc) => {
          if (!doc.uploaded_at) return latest;
          return !latest || moment(doc.uploaded_at).isAfter(latest.uploaded_at) ? doc : latest;
        }, null);

        return latestDocument ? (
          <div className="mb-5">
            <p className="mb-0">
              {latestDocument.uploaded_at
                ? `${moment(latestDocument.uploaded_at).format('DD-MM-YYYY HH:mm:ss')} hs`
                : ''}
            </p>
          </div>
        ) : null;
      }
      case 'result_text':
        return item.result_text ?? '-';
      case 'actions':
        return (
          <a rel="noopener noreferrer" target="_blank" href={`${publicSites[countryCode]}app/pasos-paciente/${item.code_id}`}>
            <LinkOutlined />
          </a>
        );
      case 'edit':
        return (
          <Link to={`/reevaluaciones/${item.medical_consultation_details_id}/editar`}>
            <Button className="cmt-button secondary outline" type="primary" size="small">
              Reevaluar
            </Button>
          </Link>
        );
      default:
        return item[key] ?? '-';
    }
  };

  return (
    <div className="reevaluation-table-wrapper">
      <h2>{title}</h2>

      {loading ? (
        <Spin className="cmt-spin mt-50" />
      ) : (
        <>
          <Row className="table-header">
            {columns.map((col) => (
              <Col key={col.key} lg={col.lg} xs={col.xs} className={col.headerClassName || col.className || 'center-item p-10'} align="center">
                {col.sortable ? (
                  <SortableHeader
                    title={col.title}
                    columnKey={col.key}
                    currentSort={filters.sort} // { key: 'expiration', order: 'asc' }
                    onSort={onOrderChange}
                  />
                ) : (
                  <h3 className="text-center">{col.title}</h3>
                )}
              </Col>
            ))}
          </Row>

          {data?.length ? (
            data.map((item, index) => (
              <Row className="table-body mb-15" key={index}>
                {columns.map((col) => (
                  <Col key={col.key} lg={col.lg} xs={col.xs} className={col.bodyClassName || col.className || 'center-item p-10'}>
                    <p className="mb-0">{renderCellValue(item, col.key)}</p>
                  </Col>
                ))}
                <Divider className="mt-15 mb-15" />
                <Col xs={24} lg={24}>
                  <Row className="branch-office-row m-10">
                    <Col className="branch-office-name">
                      {item?.branch_office_name}
                    </Col>
                    {countrySettings.reevaluationList.showEvaluationDateInRow?.[countryCode] && (
                      <Col flex="auto" className="center-item">
                        <p className="mb-0">
                          {item?.date ? formatDate(item.date) : ''}
                          {item?.documents?.length > 0 && (() => {
                            const latestDoc = item.documents.reduce((latest, doc) => {
                              if (!doc.uploaded_at) return latest;
                              return !latest || moment(doc.uploaded_at).isAfter(latest.uploaded_at) ? doc : latest;
                            }, null);
                            return latestDoc?.uploaded_at ? ` ${moment(latestDoc.uploaded_at).format('HH:mm:ss')} hs` : '';
                          })()}
                        </p>
                      </Col>
                    )}
                    {showActions && (
                      <Col
                        className="action-buttons"
                        flex="none"
                      >
                        <a rel="noopener noreferrer" target="_blank" href={`${publicSites[countryCode]}app/pasos-paciente/${item.code_id}`} className="mr-80">
                          <LinkOutlined />
                        </a>
                        <Link to={`/reevaluaciones/${item.medical_consultation_details_id}/editar`}>
                          <Button className="cmt-button secondary outline" type="primary" size="small">
                            Gestionar reevaluación
                          </Button>
                        </Link>
                      </Col>
                    )}
                  </Row>
                </Col>
              </Row>
            ))
          ) : (
            <Empty description={<span style={{ fontSize: 12 }}>No hay reevaluaciones disponibles</span>} image={Empty.PRESENTED_IMAGE_SIMPLE} />
          )}

          {pagination && (
            <Row justify="center" align="middle" gutter={16} style={{ marginTop: 24, marginBottom: 24 }}>
              <Col>
                <Button className="cmt-button" onClick={() => onPageChange('prev')} disabled={filters?.page <= 1}>
                  &lt;
                </Button>
              </Col>
              <Col>
                <span>
                  {filters?.page} / {pagination.lastPage === 0 ? 1 : pagination.lastPage}
                </span>
              </Col>
              <Col>
                <Button className="cmt-button" onClick={() => onPageChange('next')} disabled={filters?.page >= pagination.lastPage}>
                  &gt;
                </Button>
              </Col>
            </Row>
          )}
        </>
      )}
    </div>
  );
};

export default ReevaluationTable;
